import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useSeries from '../../hooks/useSeries';
import useVariations from '../../hooks/useVariations';
import {
    fetchSeriesVariationRates,
    createSeriesVariationRate,
    updateSeriesVariationRate,
    deleteSeriesVariationRate,
} from '../../api/seriesVariationRate';
import LoadingSpinner from '../../components/LoadingSpinner';

const AdminSeriesVariationsPage = () => {
    const seriesList = useSeries();
    const variations = useVariations();
    const [selectedSeriesID, setSelectedSeriesID] = useState('');
    const [rates, setRates] = useState([]);
    const [form, setForm] = useState({ variationID: '', cardsPerHit: '' });
    const [editingID, setEditingID] = useState(null);

    useEffect(() => {
        if (!selectedSeriesID) {
            setRates([]);
            return;
        }
        const loadRates = async () => {
            try {
                const data = await fetchSeriesVariationRates(selectedSeriesID);
                setRates(data);
            } catch {
                toast.error('Failed to load rates');
            }
        };
        loadRates();
    }, [selectedSeriesID]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingID) {
                await updateSeriesVariationRate(editingID, { cardsPerHit: Number(form.cardsPerHit) });
                toast.success('Rate updated');
            } else {
                await createSeriesVariationRate({
                    seriesID: selectedSeriesID,
                    variationID: form.variationID,
                    cardsPerHit: Number(form.cardsPerHit),
                });
                toast.success('Rate created');
            }
            setForm({ variationID: '', cardsPerHit: '' });
            setEditingID(null);
            const data = await fetchSeriesVariationRates(selectedSeriesID);
            setRates(data);
        } catch {
            toast.error('Failed to save rate');
        }
    };

    const handleEdit = (rate) => {
        setEditingID(rate.seriesVariationRateID);
        setForm({ variationID: rate.variationID, cardsPerHit: rate.cardsPerHit });
    };

    const handleDelete = async (id) => {
        try {
            await deleteSeriesVariationRate(id);
            toast.success('Rate deleted');
            const data = await fetchSeriesVariationRates(selectedSeriesID);
            setRates(data);
        } catch {
            toast.error('Failed to delete rate');
        }
    };

    const handleCancel = () => {
        setEditingID(null);
        setForm({ variationID: '', cardsPerHit: '' });
    };

    if (seriesList.length === 0) return <LoadingSpinner />;

    return (
        <div className="admin-page">
            <h2>Series Variation Rates</h2>

            <div className="admin-selector">
                <label>Series:</label>
                <select
                    value={selectedSeriesID}
                    onChange={e => setSelectedSeriesID(e.target.value)}
                >
                    <option value="">Select a series</option>
                    {seriesList.map(s => (
                        <option key={s.seriesID} value={s.seriesID}>{s.name}</option>
                    ))}
                </select>
            </div>

            {selectedSeriesID && (
                <>
                    <form onSubmit={handleSubmit} className="admin-form">
                        {!editingID && (
                            <div className="admin-field">
                                <label>Variation</label>
                                <select
                                    value={form.variationID}
                                    onChange={e => setForm({ ...form, variationID: e.target.value })}
                                    required
                                >
                                    <option value="">Select variation</option>
                                    {variations.map(v => (
                                        <option key={v.variationID} value={v.variationID}>{v.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="admin-field">
                            <label>Cards Per Hit (1 in X)</label>
                            <input
                                type="number"
                                min="1"
                                value={form.cardsPerHit}
                                onChange={e => setForm({ ...form, cardsPerHit: e.target.value })}
                                required
                            />
                        </div>
                        <button type="submit">{editingID ? 'Update' : 'Add'}</button>
                        {editingID && <button type="button" className="boring_button" onClick={handleCancel}>Cancel</button>}
                    </form>

                    <div className="table-scroll-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Variation</th>
                                    <th>Cards Per Hit</th>
                                    <th>Rate</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rates.map(r => (
                                    <tr key={r.seriesVariationRateID}>
                                        <td>{r.CardVariation?.name || r.variationID}</td>
                                        <td>1 in {r.cardsPerHit}</td>
                                        <td>{(100 / r.cardsPerHit).toFixed(2)}%</td>
                                        <td>
                                            <div className="actions-cell">
                                                <button className="less-action" onClick={() => handleEdit(r)}>Edit</button>
                                                <button className="danger-action" onClick={() => handleDelete(r.seriesVariationRateID)}>Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {rates.length === 0 && (
                                    <tr><td colSpan="4" className="admin-empty">No variation rates configured for this series.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminSeriesVariationsPage;
